import { createPortal } from "react-dom";
import WappIcono from "../../../../components/wappIcono";
import React, { useEffect, useState, useSyncExternalStore } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";

import HttpManager from "../../../../services/HttpManager";
import { PbxDirectory } from "../../../../services/v2/prospects.service";
import { telefonoBonito } from "../../../../utils/telefonoBonito";
import { errorTexto } from "../salesHelpers";
import { aDestino, limpiarMarcado, telefono } from "./telefono";
import { useArrastre } from "../arrastre";

/** El estado del teléfono para React (se vuelve a pintar cuando cambia). */
let version = 0;
telefono.suscribir(() => { version++; });
export const useTelefono = () => {
	useSyncExternalStore((cb) => telefono.suscribir(cb), () => version);
	return telefono;
};

const reloj = (desde: number | null): string => {
	if (!desde) return "";
	const s = Math.max(0, Math.round((Date.now() - desde) / 1000));
	return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
};

/**
 * Traer al frente el panel de la llamada en curso: a su lugar y sin minimizar (Kency, 2026-10-19: "no aparece la llamada,
 * no se puede cortar"). Lo usan el botón del teléfono de la barra y el botón "En llamada" de la ficha.
 */
const avisosMostrar = new Set<() => void>();
export const mostrarLlamada = () => avisosMostrar.forEach((f) => f());
const enCurso = (t: typeof telefono) => !!t.llamada && !t.llamada.equipo && t.llamada.status !== "terminada";

const TECLAS = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "*", "0", "#"];
const LETRAS: { [k: string]: string } = { 2: "ABC", 3: "DEF", 4: "GHI", 5: "JKL", 6: "MNO", 7: "PQRS", 8: "TUV", 9: "WXYZ", 0: "+" };

/**
 * Botón del teléfono (arriba a la derecha de CRM, WappChat y WappTeam): estado, extensión y marcador.
 * Siempre encendido (Esteban, 2026-10-12: "todas deben tener el tel encendido all the time"): se conecta solo al abrir,
 * sin preguntar por el micrófono (se pide hasta la primera llamada) y ya no se puede apagar. Si no está listo, el clic
 * fuerza la reconexión (antes, con la conexión atorada en "Conectando", el clic no hacía nada).
 */
export const TelefonoBoton: React.FC<{ campusId: number; compacto?: boolean }> = ({ campusId, compacto }) => {
	const tel = useTelefono();
	const [aviso, setAviso] = useState<string | null>(null);
	useEffect(() => {
		tel.usarFuente(async () => {
			const r = await HttpManager.getInstance().prospectsService.pbx(campusId);
			return r.data.enabled ? r.data : null;
		});
		// Solo si no hay conexión: al cambiar de pantalla o de plantel la que ya está sigue (la extensión es de la persona).
		if (tel.estado === "apagado") tel.reiniciar(false).catch(() => null);
	}, [campusId]);
	const reconectar = async () => {
		setAviso(null);
		try {
			const r = await HttpManager.getInstance().prospectsService.pbx(campusId);
			if (!r.data.enabled) { setAviso("El teléfono de WAPP todavía no está conectado a las líneas."); return; }
			await tel.reiniciar(true);
		} catch (e) { setAviso(errorTexto(e)); }
	};
	const [abierto, setAbierto] = useState<boolean>(false);
	const [numero, setNumero] = useState<string>("");
	// En el celular el teclado propio reemplaza al del sistema (si no, tapa el marcador).
	const tactil = typeof window !== "undefined" && window.matchMedia?.("(pointer: coarse)").matches;
	const marcar = () => {
		const d = aDestino(numero);
		if (!d) return;
		if (tel.llamar(d, {})) { setNumero(""); setAbierto(false); }
	};
	// En llamada el clic trae el panel de la llamada (no se puede marcar otra mientras tanto).
	const llamando = enCurso(tel);
	const txt = llamando ? "Ver la llamada" : tel.estado === "listo" ? `Teléfono · ext ${tel.creds?.extension}` : tel.estado === "error" ? "Teléfono: reconectar" : "Conectando teléfono…";
	return (
		<span className={"tel-boton-caja" + (compacto ? " compacto" : "")}>
			<button className={"btn-wa tel-boton " + tel.estado} aria-expanded={tel.estado === "listo" && !llamando ? abierto : undefined}
				onClick={llamando ? () => { setAbierto(false); mostrarLlamada(); } : tel.estado === "listo" ? () => setAbierto(!abierto) : () => reconectar()} aria-label={txt}
				title={llamando ? "Ver la llamada (colgar, silenciar, transferir)" : tel.estado === "listo" ? "Marcar un número" : (tel.error || txt) + " Clic para reconectar."}>
				<span className="tel-punto" aria-hidden="true" />
				{compacto ? (
					<WappIcono n="phone" size={18} />
				) : txt}
			</button>
			{aviso || (tel.estado === "error" && tel.error) ? <span className="tel-aviso">{aviso || tel.error}</span> : null}
			{abierto && tel.estado === "listo" && !llamando ? createPortal(
				<>
					<span className="tel-fondo" onClick={() => setAbierto(false)} aria-hidden="true" />
					<span className="tel-menu" role="dialog" aria-label="Marcador">
						<span className="tel-menu-cab">
							<span className="tel-ext">{tel.creds?.name ? tel.creds.name + " · " : ""}ext {tel.creds?.extension}</span>
							<button className="tel-x" onClick={() => setAbierto(false)} aria-label="Cerrar marcador"><WappIcono n="x" size={22} /></button>
						</span>
						<input id="tel-num" className="tel-pantalla" type="tel" value={numero} autoFocus={!tactil} inputMode={tactil ? "none" : "tel"}
							placeholder="Número o extensión" aria-label="Número a marcar"
							onChange={(e) => setNumero(limpiarMarcado(e.target.value))} onKeyDown={(e) => { if (e.key === "Enter") marcar(); if (e.key === "Escape") setAbierto(false); }} />
						<span className="tel-pad">
							{TECLAS.map((k) => (
								<button key={k} type="button" className="tel-tecla" onClick={() => setNumero((n) => limpiarMarcado(n + k))} aria-label={k}>
									<b>{k}</b><small>{LETRAS[k] || ""}</small>
								</button>
							))}
						</span>
						<span className="tel-pad-acc">
							<span />
							<button className="tel-llamar" disabled={!numero.trim() || tel.ocupado} onClick={marcar} aria-label="Llamar"><WappIcono n="phone" size={28} /></button>
							<button className="tel-borrar" disabled={!numero} onClick={() => setNumero((n) => n.slice(0, -1))} aria-label="Borrar"><WappIcono n="backspace" size={24} /></button>
						</span>
					</span>
				</>, document.body
			) : null}
		</span>
	);
};

/**
 * Panel de la llamada en curso (abajo a la derecha, encima de todo): quién, cuánto lleva, silenciar, espera, teclado,
 * transferir (directa o con aviso) y colgar. Para la entrante: contestar, rechazar y abrir su ficha.
 */
// Paneles de pantalla (CRM, WappChat): mientras haya uno montado, el global no se dibuja (no salen dos).
let panelesLocales = 0;
const avisosPaneles = new Set<() => void>();
const cambioPaneles = (d: number) => { panelesLocales += d; avisosPaneles.forEach((f) => f()); };

export const TelefonoPanel: React.FC<{ campusId: number; onAbrir: (prospectId: number) => void; global?: boolean }> = ({ campusId, onAbrir, global }) => {
	const tel = useTelefono();
	const l = tel.llamada;
	// Llamadas de WappTeam: las enseña su propia pantalla (llamadasEquipo.tsx).
	const deEquipo = !!l?.equipo;
	const [quienEs, setQuienEs] = useState<string | null>(null);
	useEffect(() => { if (global) return; cambioPaneles(1); return () => cambioPaneles(-1); }, [global]);
	// Entrante: al timbrar ya se busca de quién es el número (nombre, "Cliente de…" o "Número nuevo").
	useEffect(() => {
		setQuienEs(null);
		if (!l || l.direction !== "in" || l.number.length <= 4 || l.status !== "entrante") return;
		let vivo = true;
		HttpManager.getInstance().prospectsService.lookupPhone(campusId, "+" + l.number.replace(/\D/g, "")).then((r) => {
			if (!vivo) return;
			const m = r.data.match;
			if (!m?.id) { setQuienEs("Número nuevo"); return; }
			const nombre = [m.first_name, m.last_name].filter(Boolean).join(" ");
			tel.ligar(m.id, nombre || l.name);
			const duena = String(m.owner_user_name || m.owner_zoho_name || "").trim().split(/\s+/).slice(0, 2).join(" ");
			setQuienEs(duena ? "Cliente de " + duena : "Cliente sin asesora");
		}).catch(() => null);
		return () => { vivo = false; };
	}, [l?.callId, l?.status]);
	const [, tick] = useState(0);
	const [modo, setModo] = useState<"teclado" | "transferir" | null>(null);
	const [dir, setDir] = useState<PbxDirectory | null>(null);
	const [destino, setDestino] = useState<string>("");
	const [buscando, setBuscando] = useState<boolean>(false);

	useEffect(() => { const t = setInterval(() => tick((x) => x + 1), 1000); return () => clearInterval(t); }, []);
	useEffect(() => { if (!l) { setModo(null); setDestino(""); } }, [l]);
	useEffect(() => {
		if (modo === "transferir" && !dir) HttpManager.getInstance().prospectsService.pbxDirectory(campusId).then((r) => setDir(r.data)).catch(() => setDir({ extensions: [], teams: [] }));
	}, [modo]);

	// Entrante: buscar a quién es el número para abrir su ficha.
	const abrirFicha = async () => {
		if (!l) return;
		if (l.prospectId) { onAbrir(l.prospectId); return; }
		setBuscando(true);
		try {
			const r = await HttpManager.getInstance().prospectsService.lookupPhone(campusId, "+" + l.number.replace(/\D/g, ""));
			if (r.data.match?.id) { tel.ligar(r.data.match.id, [r.data.match.first_name, r.data.match.last_name].filter(Boolean).join(" ")); onAbrir(r.data.match.id); }
		} catch (e) { /* sin ficha: se puede dar de alta desde Nuevo prospecto */ }
		finally { setBuscando(false); }
	};

	// Que no estorbe para escribir (Esteban, 2026-10-13): en computadora se arrastra desde su encabezado y en cualquier
	// pantalla se minimiza a una pastilla arriba (en el celular ocupaba toda la pantalla y no dejaba contestarle por WhatsApp).
	// Entera: siempre completa en la pantalla, para que el botón de colgar nunca quede afuera.
	const arrastre = useArrastre("tel-panel", "abajo-derecha", true);
	const arrastrePastilla = useArrastre("tel-pastilla", "arriba-centro", true);
	const [chico, setChico] = useState<boolean>(false);
	useEffect(() => { setChico(false); }, [l?.callId]);
	useEffect(() => {
		const f = () => { setChico(false); arrastre.volver(); arrastrePastilla.volver(); };
		avisosMostrar.add(f);
		return () => { avisosMostrar.delete(f); };
	}, []);
	const esCel = typeof window !== "undefined" && !!window.matchMedia?.("(max-width: 700px)").matches;

	if (!l || deEquipo) return null;
	const interna = l.number.length <= 4;
	const quien = l.name || (interna ? "Extensión " + l.number : telefonoBonito("+" + l.number));
	const estadoTxt = l.status === "entrante" ? "Llamada entrante" : l.status === "marcando" ? "Marcando…" : l.status === "sonando" ? "Sonando…"
		: l.status === "terminada" ? (l.motivo ? "No se pudo completar" : "Terminó") : (l.held ? "En espera · " : "") + reloj(l.startedAt);

	if (chico && l.status !== "entrante" && l.status !== "terminada") {
		return createPortal(
			<div className="tel-pastilla" ref={arrastrePastilla.ref} style={arrastrePastilla.style} role="status" {...arrastrePastilla.agarre}>
				<WappIcono n="phone" size={18} />
				<button type="button" className="tel-pastilla-tx" onClick={() => setChico(false)} title="Abrir la llamada"><b>{quien}</b><span>{estadoTxt}</span></button>
				<button type="button" className="tel-pastilla-ico" onClick={() => tel.silenciar()} aria-label={l.muted ? "Activar micro" : "Silenciar"} aria-pressed={l.muted}><WappIcono n={l.muted ? "micOff" : "mic"} size={18} /></button>
				<button type="button" className="tel-pastilla-ico colgar" onClick={() => tel.colgar()} aria-label="Colgar"><WappIcono n="phoneOff" size={18} /></button>
			</div>, document.body);
	}

	return (
		createPortal(<div className={"tel-panel " + l.status} role="dialog" aria-label="Llamada" ref={esCel ? undefined : arrastre.ref} style={esCel ? undefined : arrastre.style}>
			{l.status !== "entrante" && l.status !== "terminada" ? (
				<button type="button" className="tel-minimizar" onClick={() => setChico(true)} aria-label="Minimizar" title="Minimizar para seguir escribiendo"><WappIcono n="minus" size={18} /></button>
			) : null}
			<div className="tel-cab" {...(esCel ? {} : arrastre.agarre)}>
				<div className="tel-avatar" aria-hidden="true">{l.name ? l.name.split(" ").filter(Boolean).slice(0, 2).map((x) => x[0]).join("").toUpperCase() : <WappIcono n="user" size={30} />}</div>
				<div className="tel-ident">
					<div className="tel-quien">{quien}</div>
					{l.name && !interna ? <div className="tel-numero">{telefonoBonito("+" + l.number)}</div> : null}
					<div className="tel-estado">{l.direction === "in" ? "Entrante · " : ""}{estadoTxt}{l.muted ? " · micrófono apagado" : ""}</div>
					{quienEs && l.direction === "in" ? <div className="tel-quien-es">{quienEs}</div> : null}
				</div>
				{!interna ? <button className="tel-ficha" disabled={buscando} onClick={abrirFicha}><WappIcono n="idBadge" size={16} /> Ver cliente</button> : null}
			</div>

			{l.status === "terminada" && l.motivo ? (
				<div className="tel-motivo" role="alert">
					<span>{l.motivo}</span>
					<button className="btn-wa" onClick={() => tel.cerrarAviso()}>Cerrar</button>
				</div>
			) : null}
			{l.status === "entrante" ? (
				<div className="tel-redondos dos">
					<span><button className="tel-red colgar" onClick={() => tel.colgar()} aria-label="Rechazar"><WappIcono n="phoneOff" size={30} /></button><small>Rechazar</small></span>
					<span><button className="tel-red contestar" onClick={() => { tel.contestar(); abrirFicha(); }} aria-label="Contestar"><WappIcono n="phone" size={30} /></button><small>Contestar</small></span>
				</div>
			) : l.status !== "terminada" ? (
				<>
					{l.consult ? (
						<div className="tel-consulta">
							<span>Con {l.consult.number}: {l.consult.status === "en_llamada" ? "hablando (el cliente espera)" : "marcando…"}</span>
							<div className="tel-acc">
								<button className="btn-wa pri" disabled={l.consult.status !== "en_llamada"} onClick={() => tel.completarTransferencia()}>Pasarle la llamada</button>
								<button className="btn-wa" onClick={() => tel.cancelarConsulta()}>Regresar con el cliente</button>
							</div>
						</div>
					) : null}
					<div className="tel-redondos">
						<span><button className={"tel-red" + (l.muted ? " on" : "")} aria-pressed={l.muted} onClick={() => tel.silenciar()} aria-label="Silenciar"><WappIcono n={l.muted ? "micOff" : "mic"} size={26} /></button><small>{l.muted ? "Activar micro" : "Silenciar"}</small></span>
						<span><button className={"tel-red" + (modo === "teclado" ? " on" : "")} onClick={() => setModo(modo === "teclado" ? null : "teclado")} aria-label="Teclado"><WappIcono n="keypad" size={26} /></button><small>Teclado</small></span>
						<span><button className={"tel-red" + (l.held ? " on" : "")} aria-pressed={l.held} disabled={!l.startedAt} onClick={() => tel.espera()} aria-label="Espera"><WappIcono n="pause" size={26} /></button><small>{l.held ? "Quitar espera" : "Espera"}</small></span>
						<span><button className={"tel-red" + (modo === "transferir" ? " on" : "")} disabled={!l.startedAt || !!l.consult} onClick={() => setModo(modo === "transferir" ? null : "transferir")} aria-label="Transferir"><WappIcono n="transfer" size={26} /></button><small>Transferir</small></span>
					</div>
					{modo === "teclado" ? (
						<div className="tel-pad">{TECLAS.map((k) => <button key={k} type="button" className="tel-tecla" onClick={() => tel.tecla(k)} aria-label={k}><b>{k}</b><small>{LETRAS[k] || ""}</small></button>)}</div>
					) : null}
					{modo === "transferir" ? (
						<div className="tel-transferir">
							<select value={destino} onChange={(e) => setDestino(e.target.value)} aria-label="Transferir a">
								<option value="">{dir ? "¿A quién?" : "Cargando…"}</option>
								{dir?.supervision?.length ? <optgroup label="Supervisión">{dir.supervision.map((x) => <option key={"s" + x.extension} value={x.extension}>{x.name} · {x.extension}</option>)}</optgroup> : null}
								{dir?.teams.length ? <optgroup label="Equipos (suena en todas)">{dir.teams.map((t) => <option key={t.extension} value={t.extension}>{t.team} · {t.extension}</option>)}</optgroup> : null}
								{dir ? <optgroup label="Personas">{dir.extensions.filter((x) => x.extension !== tel.creds?.extension).map((x) => <option key={x.extension} value={x.extension}>{x.name} · {x.extension}</option>)}</optgroup> : null}
							</select>
							<div className="tel-acc">
								<button className="btn-wa" disabled={!destino} onClick={() => { tel.consultar(destino); setModo(null); }}>Con aviso</button>
								<button className="btn-wa pri" disabled={!destino} onClick={() => { tel.transferir(destino); setModo(null); }}>Directa</button>
							</div>
						</div>
					) : null}
					<div className="tel-colgar-fila"><button className="tel-red colgar" onClick={() => tel.colgar()} aria-label="Colgar"><WappIcono n="phoneOff" size={30} /></button></div>
				</>
			) : null}
		</div>, document.body)
	);
};

/**
 * La llamada en cualquier pantalla de WAPP (Esteban, 2026-10-09: "¿cómo se van a mostrar las entrantes?"). Antes solo se
 * veía en CRM y WappChat: en WappTeam u otra pantalla timbraba sin dónde contestar. Va en app.tsx.
 */
export const TelefonoGlobal: React.FC = () => {
	const tel = useTelefono();
	const navigate = useNavigate();
	const { pathname } = useLocation();
	const locales = useSyncExternalStore((f) => { avisosPaneles.add(f); return () => { avisosPaneles.delete(f); }; }, () => panelesLocales);
	const sessionCampus: any = useSelector((st: any) => st.rootReducer?.sessionState?.currentCampus);
	const legacyKey: any = useSelector((st: any) => st.currentUserCampus?.value);
	const legacyRef: any = useSelector((st: any) => st.campusRef?.value);
	const campusId: number | null = (legacyKey && legacyRef ? legacyRef[legacyKey]?.id : null) ?? sessionCampus?.id ?? null;
	if (!tel.llamada || tel.llamada.equipo || !campusId || locales > 0 || pathname === "/access") return null;
	return <TelefonoPanel campusId={campusId} global onAbrir={(id) => navigate("/prospects?id=" + id)} />;
};
